import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import MobileFilterRail from "@/components/MobileFilterRail";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, CheckCircle2, XCircle, ClipboardX, Info } from "lucide-react";

const STATUS_META = {
  pending:  { label: "قيد المراجعة", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "تم القبول",     cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "تم الرفض",       cls: "bg-slate-50 text-slate-700 border-slate-200" },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; } };

export default function PlacementCancellations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [modalFor, setModalFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/placement-cancellations");
      setItems(data.cancellations || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: items.length,
    pending: items.filter((c) => c.status === "pending").length,
    approved: items.filter((c) => c.status === "approved").length,
    rejected: items.filter((c) => c.status === "rejected").length,
  }), [items]);

  const filtered = filter ? items.filter((c) => c.status === filter) : items;

  return (
    <DashboardLayout>
      <div data-testid="placement-cancellations">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إدارة التسكين</div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-heading font-black text-slate-900">طلبات إلغاء التسكين</h1>
          <p className="mt-2 text-slate-600 max-w-3xl text-sm">
            طلبات مقدَّمة من الطلاب لإلغاء تسكينهم في فرصة داخل جمعية. اعتمادها يفرج عن المقعد ويخطر الأطراف.
          </p>
        </div>

        <MobileFilterRail
          value={filter}
          onChange={setFilter}
          filters={[
            { id: "", label: "الكل", count: counts.all },
            { id: "pending", label: "قيد المراجعة", count: counts.pending },
            { id: "approved", label: "تم القبول", count: counts.approved },
            { id: "rejected", label: "تم الرفض", count: counts.rejected },
          ]}
        />

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
            <ClipboardX className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <div className="text-sm">
              {items.length === 0 ? "لا توجد طلبات إلغاء تسكين حتى الآن." : "لا نتائج مطابقة للفلتر."}
            </div>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {filtered.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.pending;
              return (
                <article key={c.id} className="bg-white rounded-2xl border border-cream-200 p-4 card-shadow" data-testid={`cancellation-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-black text-slate-900 truncate">{c.student_name}</div>
                        {c.initiated_by === "admin" && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 text-[10px] font-black px-2 py-0.5 border border-rose-200" data-testid={`admin-initiated-${c.id}`} title="إلغاء مباشر بقرار الإدارة">
                            إلغاء مباشر
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 arabic-numerals" dir="ltr">{c.student_email}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.cls} shrink-0`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="mt-3 text-sm font-semibold text-teal-800 line-clamp-2">
                    {c.opportunity_title || "—"}
                  </div>
                  {c.association_name && (
                    <div className="mt-1 text-xs text-slate-600">لدى: {c.association_name}</div>
                  )}
                  <div className="mt-3 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-700 leading-6 line-clamp-3">
                    <b className="text-slate-800">السبب:</b> {c.reason}
                  </div>
                  {c.admin_note && (
                    <div className="mt-2 rounded-lg bg-teal-50 border border-teal-100 p-2.5 text-xs text-teal-900 leading-6">
                      <b>ملاحظة الإدارة:</b> {c.admin_note}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 arabic-numerals">
                    <span>قُدّم في {fmtDate(c.created_at)}</span>
                    {c.decided_at && <span>{STATUS_META[c.status]?.label} {fmtDate(c.decided_at)}</span>}
                  </div>
                  {c.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => setModalFor(c)}
                      className="mt-3 inline-flex items-center justify-center gap-1 min-h-11 w-full rounded-xl bg-teal-700 text-white text-xs font-bold hover:bg-teal-600"
                      data-testid={`review-${c.id}`}
                    >
                      مراجعة الطلب
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {modalFor && (
          <ReviewModal
            request={modalFor}
            onClose={() => setModalFor(null)}
            onDone={() => { setModalFor(null); load(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function ReviewModal({ request, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/placement-cancellations/${request.id}`, { status, admin_note: note.trim() });
      toast.success(status === "approved" ? "تم قبول طلب الإلغاء" : "تم رفض طلب الإلغاء");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:px-4" data-testid="cancellation-review-modal">
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col animate-sheet-up sm:animate-fade-in-fast" style={{ maxHeight: "92dvh" }}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مراجعة طلب</div>
            <h2 className="mt-1 text-lg font-heading font-black text-slate-900">
              {request.student_name}
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">{request.opportunity_title}</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 min-h-11 min-w-11 grid place-items-center" data-testid="review-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700 leading-6">
            <div className="text-[11px] font-black text-slate-500 uppercase mb-1">سبب الطالب</div>
            {request.reason}
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 leading-6 flex gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>القبول يعيد الطلب إلى «مرفوض» ويحرّر مقعد الفرصة، ويُخطر الجمعية والموجّه. الرفض يُخطر الطالب فقط.</span>
          </div>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">ملاحظة الإدارة (اختيارية)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              placeholder="ستُرسَل هذه الملاحظة إلى الطالب مع القرار…"
              data-testid="review-note"
            />
          </label>
        </div>
        <div className="flex gap-2 p-5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <button
            type="button"
            onClick={() => decide("rejected")}
            disabled={busy}
            className="flex-1 min-h-11 rounded-xl bg-slate-800 text-white text-sm font-bold hover:bg-slate-900 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="review-reject"
          >
            <XCircle className="h-4 w-4" /> رفض الطلب
          </button>
          <button
            type="button"
            onClick={() => decide("approved")}
            disabled={busy}
            className="flex-1 min-h-11 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="review-approve"
          >
            <CheckCircle2 className="h-4 w-4" /> قبول الإلغاء
          </button>
        </div>
      </div>
    </div>
  );
}
